import { usePageMeta } from "@/components/pepmuse/usePageMeta";
import { LegalLayout, LegalSection } from "@/components/pepmuse/LegalLayout";

export default function Privacy() {
  usePageMeta("Privacy Policy — PepMuse", "How PepMuse collects, uses, and protects your information.");

  return (
    <LegalLayout testId="privacy-page" eyebrow="Legal" title="Privacy Policy" updated="September 6, 2026">
      <LegalSection title="Who we are">
        <p>
          PepMuse ("we", "us") provides branding, website creation, and launch support services for people starting
          their own research peptide brands. We are a services studio — we do not sell peptides, supplements, or any
          regulated products on this site.
        </p>
      </LegalSection>

      <LegalSection title="What we collect">
        <p>
          <strong className="text-paper/85">Details you share with us.</strong> When you send a project inquiry or
          generate a launch proposal, we collect the information you type in: your name, email address, phone number,
          business name, what you need, and anything you tell us about your idea — along with the checklist
          selections behind your proposal.
        </p>
        <p>
          <strong className="text-paper/85">Technical information.</strong> Like most sites, we (and our hosting and
          analytics providers) process basic technical data such as browser type, device, approximate region, and
          pages viewed, so the site stays fast, secure, and improving.
        </p>
        <p>
          <strong className="text-paper/85">Stored on your device.</strong> Your launch checklist progress and contact
          details are also saved in your browser's local storage, purely so you can leave and pick up where you left
          off. You can clear it at any time through your browser settings or the checklist's "Start over" control.
        </p>
      </LegalSection>

      <LegalSection title="How we use it">
        <p>We use what we collect to:</p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li>Respond to your inquiry and prepare your proposal</li>
          <li>Deliver and support the services you ask us for</li>
          <li>Keep the site secure and understand how it's used so we can improve it</li>
        </ul>
      </LegalSection>

      <LegalSection title="What we never do">
        <p>
          We do not sell your personal information, rent it, or share it with data brokers. We do not send marketing
          emails you didn't ask for. We do not use your project details for anything other than your project.
        </p>
      </LegalSection>

      <LegalSection title="Who we share it with">
        <p>
          Only the service providers that keep PepMuse running — hosting, database, and email delivery — and only as
          much as they need to do their job. We may also disclose information if the law requires it or to protect the
          safety and rights of our business and others.
        </p>
      </LegalSection>

      <LegalSection title="Retention and security">
        <p>
          We keep inquiry and proposal records for as long as they're useful to your project and our legitimate
          business records, then delete or anonymize them. We apply reasonable technical and organizational safeguards;
          no internet transmission is perfectly secure, but we treat your details with care.
        </p>
      </LegalSection>

      <LegalSection title="Your rights">
        <p>
          Depending on where you live (including California under the CCPA/CPRA and similar state laws), you may have
          the right to access, correct, delete, or receive a copy of your personal information, and to opt out of any
          sale or sharing — though, to be clear, we don't sell or share it in that sense.
        </p>
        <p>
          To exercise any of these rights, send us a note through the project inquiry form on this site with "Privacy
          request" as your message, and we'll take care of it.
        </p>
      </LegalSection>

      <LegalSection title="Cookies and local storage">
        <p>
          This site uses browser local storage for convenience features (like remembering your checklist) and may use
          lightweight analytics. We don't use advertising trackers.
        </p>
      </LegalSection>

      <LegalSection title="Age">
        <p>
          PepMuse is a business-to-business service intended for adults. This site is not directed at anyone under 18,
          and we don't knowingly collect their information.
        </p>
      </LegalSection>

      <LegalSection title="Changes and contact">
        <p>
          If we update this policy, we'll post the new version here with a fresh "last updated" date. Questions about
          privacy? Reach us through the project inquiry form on this site.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
